import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { HousekeepingRoomStatus } from '../../../pages/housekeeping/types/housekeeping';
import Button from '../../components/Button';
import Text from '../../components/Text';
import { repartirChambres } from '../../helpers/menage';
import { t } from '../../i18n/langue';

import CarteChambre from './CarteChambre';

/**
 * Les chambres du jour, par section — l'ordre du web (`MyRooms.tsx`) : En cours,
 * À faire, À préparer, puis « Autres » repliée. Les chambres déjà faites ne sont
 * pas le travail du moment ; les déplier est un choix.
 */
function Section({ titre, chambres }: { titre: string; chambres: HousekeepingRoomStatus[] }) {
    const navigate = useNavigate();
    if (chambres.length === 0) return null;

    return (
        <section className="flex flex-col gap-2">
            <Text variante="sousTitre" as="h2">
                {t('menage.sections.titre', { titre, n: chambres.length })}
            </Text>
            {chambres.map((chambre) => (
                <CarteChambre
                    key={chambre.service_id}
                    chambre={chambre}
                    onOuvrir={() => navigate(`/housekeeping/${chambre.service_id}`)}
                />
            ))}
        </section>
    );
}

export default function SectionsDuJour({ chambres }: { chambres: HousekeepingRoomStatus[] }) {
    const navigate = useNavigate();
    const [autresOuvertes, setAutresOuvertes] = useState(false);
    const { enCours, aFaire, aPreparer, autres } = repartirChambres(chambres);
    const toutEstFait = enCours.length + aFaire.length + aPreparer.length === 0;

    return (
        <div className="flex flex-col gap-5">
            {toutEstFait && (
                <Text variante="sousTitre" ton="marque">
                    {t('menage.toutEstFait')}
                </Text>
            )}

            <Section titre={t('menage.sections.enCours')} chambres={enCours} />
            <Section titre={t('menage.sections.aFaire')} chambres={aFaire} />
            <Section titre={t('menage.sections.aPreparer')} chambres={aPreparer} />

            {autres.length > 0 && (
                <section className="flex flex-col gap-2">
                    <Button
                        libelle={t('menage.sections.autres', { n: autres.length })}
                        variante="discret"
                        espacement="aucun"
                        alignement="debut"
                        ariaExpanded={autresOuvertes}
                        onPress={() => setAutresOuvertes((ouvert) => !ouvert)}
                    />
                    {autresOuvertes &&
                        autres.map((chambre) => (
                            <CarteChambre
                                key={chambre.service_id}
                                chambre={chambre}
                                onOuvrir={() => navigate(`/housekeeping/${chambre.service_id}`)}
                            />
                        ))}
                </section>
            )}
        </div>
    );
}
